import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Alert, AlertDescription } from "../components/ui/alert";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";
import { 
  MessageSquare,
  Plus,
  ArrowLeft,
  Search,
  Pin,
  Lock,
  Flag,
  CheckCircle,
  Eye,
  MessageCircle,
  Clock,
  User,
  Sparkles,
  MoreVertical,
  AlertTriangle,
  FileText,
  Send,
  ThumbsUp,
  Bell,
  BellOff,
  ChevronRight,
  Home,
  HelpCircle,
  Megaphone,
  MapPin,
  ShieldAlert,
  CloudSun,
  ShoppingBag,
  Calendar,
  Upload,
  X,
  Image,
  File,
  Loader2
} from "lucide-react";

const CATEGORY_ICONS = {
  general: <Home className="w-4 h-4" />,
  hoa_rules: <HelpCircle className="w-4 h-4" />,
  design_review: <FileText className="w-4 h-4" />,
  announcements: <Megaphone className="w-4 h-4" />,
  lost_found: <MapPin className="w-4 h-4" />,
  wildlife_safety: <ShieldAlert className="w-4 h-4" />,
  road_weather: <CloudSun className="w-4 h-4" />,
  buy_sell_trade: <ShoppingBag className="w-4 h-4" />,
  events_social: <Calendar className="w-4 h-4" />
};

const ForumPage = ({ isBoard = false }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  
  // State
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "");
  const [searchQuery, setSearchQuery] = useState("");
  const [showResolved, setShowResolved] = useState(false);
  
  // Post creation
  const [createOpen, setCreateOpen] = useState(false);
  const [newPost, setNewPost] = useState({
    category: "",
    title: "",
    body: "",
    tags: ""
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Post detail
  const [selectedPost, setSelectedPost] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  
  // AI features
  const [aiAnswerOpen, setAiAnswerOpen] = useState(false);
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiAnswer, setAiAnswer] = useState("");
  const [loadingAi, setLoadingAi] = useState(false);
  
  // Moderation (board only)
  const [flaggedContent, setFlaggedContent] = useState(null);
  const [moderationTab, setModerationTab] = useState("posts");

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/forum/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const fetchPosts = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== "all") params.append("category", selectedCategory);
      if (searchQuery) params.append("search", searchQuery);
      if (!showResolved) params.append("is_resolved", "false");
      
      const response = await axios.get(`${API}/forum/posts?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      toast.error("Failed to load posts");
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/forum/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchFlaggedContent = async () => {
    if (!isBoard) return;
    try {
      const response = await axios.get(`${API}/forum/moderation/flagged`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFlaggedContent(response.data);
    } catch (error) {
      console.error("Failed to fetch flagged content:", error);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    // Check total files limit
    if (uploadedFiles.length + files.length > 10) {
      toast.error("Maximum 10 files allowed per post");
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await axios.post(`${API}/forum/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.uploaded?.length > 0) {
        setUploadedFiles(prev => [...prev, ...response.data.uploaded]);
        toast.success(`${response.data.total_uploaded} file(s) uploaded`);
      }
      
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => {
          toast.error(`${err.filename}: ${err.error}`);
        });
      }
    } catch (error) {
      toast.error("Failed to upload files");
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeUploadedFile = async (fileId) => {
    try {
      await axios.delete(`${API}/forum/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUploadedFiles(prev => prev.filter(f => f.file_id !== fileId));
      toast.success("File removed");
    } catch (error) {
      toast.error("Failed to remove file");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCategories(), fetchPosts(), fetchStats()]);
      if (isBoard) await fetchFlaggedContent();
      setLoading(false);
    };
    loadData();
  }, [token, selectedCategory, showResolved]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (!loading) fetchPosts();
    }, 300);
    return () => clearTimeout(delaySearch);
  }, [searchQuery]);

  const handleCreatePost = async () => {
    if (!newPost.category || !newPost.title || !newPost.body) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const tags = newPost.tags.split(",").map(t => t.trim()).filter(t => t);
      const photos = uploadedFiles.map(f => f.url);
      
      await axios.post(`${API}/forum/posts`, {
        ...newPost,
        tags,
        photos
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Post created successfully");
      setCreateOpen(false);
      setNewPost({ category: "", title: "", body: "", tags: "" });
      setUploadedFiles([]);
      fetchPosts();
      fetchCategories();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create post");
    }
  };

  const handleViewPost = async (post) => {
    try {
      const response = await axios.get(`${API}/forum/posts/${post.post_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedPost(response.data);
      setDetailOpen(true);
    } catch (error) {
      toast.error("Failed to load post details");
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setSubmittingComment(true);
    
    try {
      await axios.post(`${API}/forum/posts/${selectedPost.post_id}/comments`, {
        body: newComment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Comment added");
      setNewComment("");
      // Refresh post
      const response = await axios.get(`${API}/forum/posts/${selectedPost.post_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedPost(response.data);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handlePinPost = async (postId) => {
    try {
      const response = await axios.post(`${API}/forum/posts/${postId}/pin`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchPosts();
      if (selectedPost?.post_id === postId) {
        setSelectedPost(prev => ({ ...prev, is_pinned: response.data.is_pinned }));
      }
    } catch (error) {
      toast.error("Failed to pin/unpin post");
    }
  };

  const handleLockPost = async (postId) => {
    try {
      const response = await axios.post(`${API}/forum/posts/${postId}/lock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchPosts();
      if (selectedPost?.post_id === postId) {
        setSelectedPost(prev => ({ ...prev, status: response.data.is_locked ? "locked" : "active" }));
      }
    } catch (error) {
      toast.error("Failed to lock/unlock post");
    }
  };

  const handleResolvePost = async (postId) => {
    try {
      const response = await axios.post(`${API}/forum/posts/${postId}/resolve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchPosts();
      if (selectedPost?.post_id === postId) {
        setSelectedPost(prev => ({ ...prev, is_resolved: response.data.is_resolved }));
      }
    } catch (error) {
      toast.error("Failed to resolve post");
    }
  };

  const handleFlagPost = async (postId, reason) => {
    try {
      await axios.post(`${API}/forum/posts/${postId}/flag`, null, {
        params: { reason },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Post flagged for review");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to flag post");
    }
  };

  const handleAskAI = async () => {
    if (!aiQuestion.trim()) return;
    setLoadingAi(true);
    
    try {
      const response = await axios.post(`${API}/forum/ai/rule-answer`, null, {
        params: { question: aiQuestion },
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiAnswer(response.data.answer);
    } catch (error) {
      toast.error("Failed to get AI answer");
    } finally {
      setLoadingAi(false);
    }
  };

  const handleGenerateSummary = async (postId) => {
    try {
      const response = await axios.post(`${API}/forum/posts/${postId}/ai-summary`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Summary generated");
      if (selectedPost?.post_id === postId) {
        setSelectedPost(prev => ({ ...prev, ai_summary: response.data.summary }));
      }
    } catch (error) {
      toast.error("Failed to generate summary");
    }
  };

  const handleConvertToNotice = async (postId) => {
    const reason = prompt("Enter notice type (e.g., 'noise', 'parking', 'exterior_change'):");
    if (!reason) return;
    
    try {
      const response = await axios.post(`${API}/forum/posts/${postId}/convert-to-violation`, null, {
        params: { violation_type: reason },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Converted to notice: ${response.data.violation_id}`);
      fetchPosts();
    } catch (error) {
      toast.error("Failed to convert to notice");
    }
  };

  const handleConvertToCorrespondence = async (postId) => {
    try {
      const response = await axios.post(`${API}/forum/posts/${postId}/convert-to-correspondence`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Converted to correspondence: ${response.data.correspondence_id}`);
      fetchPosts();
    } catch (error) {
      toast.error("Failed to convert to correspondence");
    }
  };

  const handleApprovePost = async (postId) => {
    try {
      await axios.post(`${API}/forum/moderation/approve/${postId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Post approved");
      fetchFlaggedContent();
      fetchPosts();
    } catch (error) {
      toast.error("Failed to approve post");
    }
  };

  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat?.label || value;
  };

  const getStatusBadge = (post) => {
    if (post.is_pinned) return <Badge className="bg-purple-500/10 text-purple-600"><Pin className="w-3 h-3 mr-1" /> Pinned</Badge>;
    if (post.status === "locked") return <Badge className="bg-gray-500/10 text-gray-600"><Lock className="w-3 h-3 mr-1" /> Locked</Badge>;
    if (post.status === "flagged") return <Badge className="bg-red-500/10 text-red-600"><Flag className="w-3 h-3 mr-1" /> Flagged</Badge>;
    if (post.is_resolved) return <Badge className="bg-green-500/10 text-green-600"><CheckCircle className="w-3 h-3 mr-1" /> Resolved</Badge>;
    return null;
  };

  const pageContent = (
    <div className={isBoard ? "p-6 lg:p-8 max-w-7xl mx-auto" : "min-h-screen bg-background"}>
      {/* Header - only for non-board */}
      {!isBoard && (
        <header className="border-b bg-card/95 backdrop-blur-sm sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/owner")} className="text-muted-foreground hover:text-foreground transition-colors duration-200">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <span className="font-serif text-lg font-semibold block">Community Forum</span>
                  <span className="text-xs text-muted-foreground">Connect with your neighbors</span>
                </div>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Page Header for Board */}
      {isBoard && (
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-primary" />
            </div>
            <div>
              <span className="font-serif text-lg font-semibold block">Community Forum</span>
              <span className="text-xs text-muted-foreground">Connect with your neighbors</span>
            </div>
          </div>
          <Dialog open={aiAnswerOpen} onOpenChange={setAiAnswerOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="hover:border-primary/50 transition-colors duration-200">
                <Sparkles className="w-4 h-4 mr-2" />
                Ask AI
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-serif">Ask AI About HOA Rules</DialogTitle>
                <DialogDescription>Get instant answers about HOA rules, CC&Rs, and community guidelines</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <Textarea
                  placeholder="What is the rule about..."
                  value={aiQuestion}
                  onChange={(e) => setAiQuestion(e.target.value)}
                  rows={3}
                  className="focus:ring-primary/20"
                />
                <Button onClick={handleAskAI} disabled={loadingAi || !aiQuestion.trim()} className="shadow-button hover:shadow-button-hover transition-all duration-300">
                  {loadingAi ? "Thinking..." : "Get Answer"}
                </Button>
                {aiAnswer && (
                  <div className="p-4 bg-muted/50 rounded-xl border border-border/50">
                    <pre className="whitespace-pre-wrap text-sm">{aiAnswer}</pre>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      <div className={isBoard ? "" : "max-w-7xl mx-auto px-4 md:px-8 py-8"}>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Categories */}
          <div className="lg:col-span-1 space-y-4">
            {/* Stats Card */}
            {stats && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Forum Stats</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Posts</span>
                    <span className="font-medium">{stats.total_posts}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Comments</span>
                    <span className="font-medium">{stats.total_comments}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Resolved</span>
                    <span className="font-medium text-green-600">{stats.resolved_posts}</span>
                  </div>
                  {isBoard && stats.flagged_posts > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Flagged</span>
                      <span className="font-medium text-red-600">{stats.flagged_posts}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Categories */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Categories</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                <Button
                  variant={selectedCategory === "" || selectedCategory === "all" ? "secondary" : "ghost"}
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => setSelectedCategory("")}
                >
                  <Home className="w-4 h-4 mr-2" />
                  All Posts
                </Button>
                {categories.map((cat) => (
                  <Button
                    key={cat.value}
                    variant={selectedCategory === cat.value ? "secondary" : "ghost"}
                    size="sm"
                    className="w-full justify-between"
                    onClick={() => setSelectedCategory(cat.value)}
                  >
                    <span className="flex items-center">
                      {CATEGORY_ICONS[cat.value] || <MessageSquare className="w-4 h-4" />}
                      <span className="ml-2 truncate">{cat.label}</span>
                    </span>
                    <Badge variant="outline" className="ml-2">{cat.post_count}</Badge>
                  </Button>
                ))}
              </CardContent>
            </Card>

            {/* Moderation Panel (Board Only) */}
            {isBoard && flaggedContent && (flaggedContent.flagged_posts?.length > 0 || flaggedContent.flagged_comments?.length > 0) && (
              <Card className="border-red-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center text-red-600">
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Moderation Queue
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    {flaggedContent.flagged_posts?.length || 0} posts, {flaggedContent.flagged_comments?.length || 0} comments flagged
                  </p>
                  <Button variant="outline" size="sm" className="w-full" onClick={() => navigate("/board/forum/moderation")}>
                    Review Flagged Content
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-4">
            {/* Search & Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search posts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant={showResolved ? "secondary" : "outline"}
                  size="sm"
                  onClick={() => setShowResolved(!showResolved)}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {showResolved ? "Hide" : "Show"} Resolved
                </Button>
                <Dialog open={createOpen} onOpenChange={(open) => {
                    setCreateOpen(open);
                    if (!open) {
                      // Reset form when closing
                      setNewPost({ category: "", title: "", body: "", tags: "" });
                      setUploadedFiles([]);
                    }
                  }}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-post-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      New Post
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create New Post</DialogTitle>
                      <DialogDescription>Share a question, update, or topic with the community</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Category *</Label>
                        <Select value={newPost.category} onValueChange={(v) => setNewPost({...newPost, category: v})}>
                          <SelectTrigger data-testid="category-select">
                            <SelectValue placeholder="Select a category" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.length === 0 ? (
                              <div className="p-2 text-sm text-muted-foreground">Loading categories...</div>
                            ) : (
                              categories.map(cat => (
                                <SelectItem key={cat.value} value={cat.value}>
                                  <span className="flex items-center gap-2">
                                    {CATEGORY_ICONS[cat.value]}
                                    {cat.label}
                                  </span>
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Title *</Label>
                        <Input
                          value={newPost.title}
                          onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                          placeholder="What's your question or topic?"
                          data-testid="post-title-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Body *</Label>
                        <Textarea
                          value={newPost.body}
                          onChange={(e) => setNewPost({...newPost, body: e.target.value})}
                          placeholder="Provide details..."
                          rows={5}
                          data-testid="post-body-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Tags (comma-separated)</Label>
                        <Input
                          value={newPost.tags}
                          onChange={(e) => setNewPost({...newPost, tags: e.target.value})}
                          placeholder="fence, rules, landscaping"
                        />
                      </div>
                      
                      {/* File Upload Section */}
                      <div className="space-y-2">
                        <Label>Photos & Documents (optional)</Label>
                        <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
                          <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,.doc,.docx"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="forum-file-upload"
                            data-testid="file-upload-input"
                          />
                          <label htmlFor="forum-file-upload" className="cursor-pointer">
                            <div className="flex flex-col items-center gap-2">
                              {uploading ? (
                                <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
                              ) : (
                                <Upload className="w-8 h-8 text-muted-foreground" />
                              )}
                              <span className="text-sm text-muted-foreground">
                                {uploading ? "Uploading..." : "Click or drag files to upload"}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                Images (JPG, PNG, GIF, WebP) & Documents (PDF, Word) • Max 10MB each • Up to 10 files
                              </span>
                            </div>
                          </label>
                        </div>
                        
                        {/* Uploaded Files Preview */}
                        {uploadedFiles.length > 0 && (
                          <div className="space-y-2 mt-3">
                            <Label className="text-xs text-muted-foreground">{uploadedFiles.length} file(s) attached</Label>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {uploadedFiles.map((file) => (
                                <div 
                                  key={file.file_id} 
                                  className="relative group border rounded-lg p-2 bg-muted/30"
                                >
                                  <div className="flex items-center gap-2">
                                    {file.is_image ? (
                                      <Image className="w-5 h-5 text-blue-500 flex-shrink-0" />
                                    ) : (
                                      <File className="w-5 h-5 text-orange-500 flex-shrink-0" />
                                    )}
                                    <div className="min-w-0 flex-1">
                                      <p className="text-xs font-medium truncate">{file.filename}</p>
                                      <p className="text-xs text-muted-foreground">{formatFileSize(file.file_size)}</p>
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => removeUploadedFile(file.file_id)}
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                      <Button 
                        onClick={handleCreatePost} 
                        disabled={!newPost.category || !newPost.title || !newPost.body}
                        data-testid="submit-post-btn"
                      >
                        Create Post
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Posts List */}
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading posts...</div>
            ) : posts.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No posts found</p>
                  <p className="text-sm text-muted-foreground mt-2">Be the first to start a discussion!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {posts.map((post) => (
                  <Card 
                    key={post.post_id} 
                    className={`hover:shadow-md transition-shadow cursor-pointer ${post.is_pinned ? 'border-purple-200 bg-purple-50/50' : ''}`}
                    onClick={() => handleViewPost(post)}
                    data-testid={`post-${post.post_id}`}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            {getStatusBadge(post)}
                            <Badge variant="outline" className="text-xs">
                              {CATEGORY_ICONS[post.category]}
                              <span className="ml-1">{getCategoryLabel(post.category)}</span>
                            </Badge>
                          </div>
                          <h3 className="font-semibold text-lg mb-1 line-clamp-1">{post.title}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{post.body}</p>
                          <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {post.owner_name || "Anonymous"}
                              {post.lot_number && ` • Lot ${post.lot_number}`}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(post.created_at).toLocaleDateString()}
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {post.view_count} views
                            </span>
                            <span className="flex items-center gap-1">
                              <MessageCircle className="w-3 h-3" />
                              {post.comment_count} comments
                            </span>
                          </div>
                          {post.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {post.tags.map((tag, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Post Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedPost && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {getStatusBadge(selectedPost)}
                      <Badge variant="outline">{getCategoryLabel(selectedPost.category)}</Badge>
                    </div>
                    <DialogTitle className="text-xl">{selectedPost.title}</DialogTitle>
                    <DialogDescription className="flex items-center gap-4 mt-2">
                      <span>{selectedPost.owner_name || "Anonymous"}</span>
                      <span>{new Date(selectedPost.created_at).toLocaleString()}</span>
                      <span>{selectedPost.view_count} views</span>
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="py-4 space-y-4">
                {/* Post Body */}
                <div className="prose prose-sm max-w-none">
                  <p className="whitespace-pre-wrap">{selectedPost.body}</p>
                </div>
                
                {selectedPost.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {selectedPost.tags.map((tag, i) => (
                      <Badge key={i} variant="secondary">{tag}</Badge>
                    ))}
                  </div>
                )}
                
                {/* Attached Photos/Documents */}
                {selectedPost.photos?.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Attachments ({selectedPost.photos.length})</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {selectedPost.photos.map((photoUrl, i) => {
                        const isImage = photoUrl.includes('forum_file_') && !photoUrl.endsWith('.pdf');
                        const fullUrl = photoUrl.startsWith('/') ? `${API.replace('/api/v1', '')}${photoUrl}` : photoUrl;
                        return (
                          <a 
                            key={i} 
                            href={fullUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="border rounded-lg p-3 hover:bg-muted/50 transition-colors flex items-center gap-2"
                          >
                            {isImage ? (
                              <Image className="w-5 h-5 text-blue-500 flex-shrink-0" />
                            ) : (
                              <File className="w-5 h-5 text-orange-500 flex-shrink-0" />
                            )}
                            <span className="text-sm truncate">Attachment {i + 1}</span>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* AI Summary */}
                {selectedPost.ai_summary && (
                  <Alert>
                    <Sparkles className="w-4 h-4" />
                    <AlertDescription>
                      <strong>AI Summary:</strong>
                      <pre className="whitespace-pre-wrap mt-2 text-sm">{selectedPost.ai_summary}</pre>
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  <Button variant="outline" size="sm" onClick={() => handleResolvePost(selectedPost.post_id)}>
                    <CheckCircle className="w-4 h-4 mr-1" />
                    {selectedPost.is_resolved ? "Unresolve" : "Mark Resolved"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleFlagPost(selectedPost.post_id, "Flagged by user")}>
                    <Flag className="w-4 h-4 mr-1" />
                    Report
                  </Button>
                  
                  {isBoard && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => handlePinPost(selectedPost.post_id)}>
                        <Pin className="w-4 h-4 mr-1" />
                        {selectedPost.is_pinned ? "Unpin" : "Pin"}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleLockPost(selectedPost.post_id)}>
                        <Lock className="w-4 h-4 mr-1" />
                        {selectedPost.status === "locked" ? "Unlock" : "Lock"}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleGenerateSummary(selectedPost.post_id)}>
                        <Sparkles className="w-4 h-4 mr-1" />
                        AI Summary
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleConvertToNotice(selectedPost.post_id)}>
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        → Notice
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleConvertToCorrespondence(selectedPost.post_id)}>
                        <FileText className="w-4 h-4 mr-1" />
                        → Correspondence
                      </Button>
                    </>
                  )}
                </div>
                
                {/* Comments Section */}
                <div className="pt-4 border-t">
                  <h4 className="font-semibold mb-4 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" />
                    Comments ({selectedPost.comments?.length || 0})
                  </h4>
                  
                  {selectedPost.status !== "locked" && (
                    <div className="flex gap-2 mb-4">
                      <Textarea
                        placeholder="Add a comment..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        rows={2}
                        className="flex-1"
                      />
                      <Button onClick={handleAddComment} disabled={submittingComment || !newComment.trim()}>
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  
                  {selectedPost.status === "locked" && (
                    <Alert className="mb-4">
                      <Lock className="w-4 h-4" />
                      <AlertDescription>This post is locked. No new comments can be added.</AlertDescription>
                    </Alert>
                  )}
                  
                  <div className="space-y-3">
                    {selectedPost.comments?.map((comment) => (
                      <div key={comment.comment_id} className="p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                          <User className="w-3 h-3" />
                          <span className="font-medium text-foreground">{comment.owner_name || "Anonymous"}</span>
                          <span>•</span>
                          <span>{new Date(comment.created_at).toLocaleString()}</span>
                          {comment.is_flagged && (
                            <Badge variant="destructive" className="text-xs">Flagged</Badge>
                          )}
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{comment.body}</p>
                      </div>
                    ))}
                    
                    {(!selectedPost.comments || selectedPost.comments.length === 0) && (
                      <p className="text-sm text-muted-foreground text-center py-4">No comments yet. Be the first to comment!</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );

  if (isBoard) {
    return <AdminSidebar>{pageContent}</AdminSidebar>;
  }
  return pageContent;
};

export default ForumPage;
